import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Phone, Mail, MapPin, Clock, CalendarDays } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE } from "@/config";
import {
  useSocials,
  useContactPhone,
  CONTACT_EMAIL,
} from "@/components/SocialLinks";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] },
};

export default function Contact() {
  const socials = useSocials();
  const { phone, href: phoneLink } = useContactPhone();
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
      <motion.div {...fadeUp}>
        <Badge variant="outline" className="border-primary/40 text-primary">
          {SITE.fullName}
        </Badge>
        <h1 className="mt-4 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
          Contactez<span className="text-primary">-nous</span>
        </h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Une question, un projet, un devis d'album ou une prod sur mesure ? Notre équipe vous
          répond rapidement, 7j/7.
        </p>
      </motion.div>

      <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <motion.div {...fadeUp}>
          <Card className="h-full border-border bg-card transition-colors hover:border-primary/50" data-testid="contact-phone-card">
            <CardContent className="p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                <Phone className="h-5 w-5 text-primary" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold">Téléphone</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Appelez-nous directement pour toute question ou réservation.
              </p>
              <a
                href={phoneLink}
                className="mt-4 inline-block font-mono-nums text-2xl font-semibold text-primary underline-offset-4 hover:underline"
                data-testid="contact-phone-link"
              >
                {phone}
              </a>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div {...fadeUp}>
          <Card className="h-full border-border bg-card transition-colors hover:border-primary/50" data-testid="contact-email-card">
            <CardContent className="p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                <Mail className="h-5 w-5 text-primary" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold">Email</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Devis album / projet complet, production instrumentale sur mesure, demandes
                spéciales.
              </p>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="mt-4 inline-block break-all text-lg font-semibold text-primary underline-offset-4 hover:underline"
                data-testid="contact-email-link"
              >
                {CONTACT_EMAIL}
              </a>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div {...fadeUp}>
          <Card className="h-full border-border bg-card" data-testid="contact-address-card">
            <CardContent className="p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                <MapPin className="h-5 w-5 text-primary" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold">Adresse</h2>
              <p className="mt-2 text-base font-medium text-foreground">
                47 avenue de la République
                <br />
                69200 Vénissieux
              </p>
              <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4 text-primary" />
                Ouvert 24h/24 — 7j/7
              </p>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div {...fadeUp}>
          <Card className="h-full border-primary/40 bg-[hsl(var(--studio-yellow)/0.05)]" data-testid="contact-socials-card">
            <CardContent className="p-6">
              <h2 className="font-heading text-lg font-semibold">Suivez-nous</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Nos coulisses, prods et sessions sur les réseaux.
              </p>
              <div className="mt-4 space-y-2.5">
                {socials.map(({ slug, name, handle, url, Icon, bubbleClass }) => (
                  <a
                    key={slug}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 transition-colors hover:border-primary/60"
                    data-testid={`contact-social-${slug}`}
                  >
                    <span className={`flex h-9 w-9 items-center justify-center rounded-full border ${bubbleClass}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>
                      <span className="block text-sm font-semibold">{name}</span>
                      <span className="block text-xs text-muted-foreground">{handle}</span>
                    </span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div {...fadeUp} className="mt-10 rounded-xl border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] p-8 text-center">
        <h3 className="font-heading text-xl font-semibold">Prêt à réserver votre session ?</h3>
        <Button size="lg" className="mt-5" asChild data-testid="contact-booking-cta">
          <Link to="/reserver">
            <CalendarDays className="mr-2 h-5 w-5" />
            Réserver un créneau
          </Link>
        </Button>
      </motion.div>
    </div>
  );
}
